<template>
  <!-- Step 4. Mirrors the mobile photo step (Android's OnboardingSelectPhotoFragment,
       iOS's OnBoardingSelectProfilePictureViewController): a grid of the HiiKER
       avatars plus the option to upload your own. The picture is optional —
       Continue is always available, as it is on mobile. -->
  <div>
    <OnBoardingStepIndicator :current="2" />

    <!-- No Back here. This is the first step after the account is created, and
         everything behind it on the stack is sign-in: the email form would be
         resubmitted for an account that now exists (auth/email-already-in-use),
         and the auth buttons are meaningless to someone already signed in. -->
    <h2 class="auth-heading">{{ $t('on_boarding.profile_photo_page.title') }}</h2>
    <p class="auth-sub">{{ $t('on_boarding.profile_photo_page.subtitle') }}</p>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>

    <ul class="presets" role="listbox" :aria-label="$t('on_boarding.profile_photo_page.presets_label')">
      <li>
        <!-- First cell, matching the mobile layout. A label wrapping the input
             keeps it operable by click and by keyboard without a second control. -->
        <label
          class="preset preset--add"
          :class="{ 'preset--selected': !!customPreview }"
          :title="$t('on_boarding.profile_photo_page.upload_your_own')"
        >
          <input
            type="file"
            accept="image/*"
            class="preset__input"
            :aria-label="$t('on_boarding.profile_photo_page.upload_your_own')"
            @change="onFile"
          >
          <img v-if="customPreview" :src="customPreview" alt="">
          <span v-else class="preset__add-content">
            <span class="material-icons preset__add-icon" aria-hidden="true">add</span>
            <span class="preset__add-label">{{ $t('on_boarding.profile_photo_page.add_photo') }}</span>
          </span>
        </label>
      </li>
      <li v-for="avatar in presets" :key="avatar.id">
        <button
          type="button"
          class="preset"
          :class="{ 'preset--selected': avatar.id === selectedPresetId }"
          role="option"
          :aria-selected="avatar.id === selectedPresetId"
          :title="labelFor(avatar.id)"
          @click="choose(avatar)"
        >
          <img :src="avatar.src" :alt="labelFor(avatar.id)" loading="lazy">
        </button>
      </li>
    </ul>

    <button type="button" class="btn pro-btn pro-btn--accent w-100 mt-2" @click="submit">
      <span class="material-icons">arrow_forward</span>
      <span>{{ continueLabel }}</span>
    </button>

  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import OnBoardingStepIndicator from './OnBoardingStepIndicator.vue';
import { PRESET_AVATARS, avatarLabel, type PresetAvatar } from '../../../utils/helpers/presetAvatars';

export default defineComponent({
  name: 'OnBoardingProfilePhotoPage',
  components: { OnBoardingStepIndicator },
  data() {
    return { presets: PRESET_AVATARS as PresetAvatar[] };
  },
  computed: {
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    preview(): string | null {
      return this.$store.state.onBoarding.profileAvatarPreview;
    },
    selectedPresetId(): string | null {
      return this.$store.state.onBoarding.profileAvatarPresetId;
    },
    // Only an uploaded photo needs its own thumbnail; a preset is already
    // shown as selected in the grid.
    customPreview(): string | null {
      const state = this.$store.state.onBoarding;
      return state.profileAvatarPresetId ? null : state.profileAvatarPreview;
    },
    // The picture is optional, so say so rather than implying a choice is due.
    continueLabel(): string {
      return this.preview
        ? this.$t('on_boarding.profile_photo_page.continue')
        : this.$t('on_boarding.profile_photo_page.skip');
    },
  },
  methods: {
    labelFor(id: string): string {
      return avatarLabel(id);
    },
    choose(avatar: PresetAvatar) {
      this.$store.dispatch('onBoarding/selectPresetAvatar', avatar);
    },
    onFile(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files && input.files[0];
      if (file) this.$store.dispatch('onBoarding/selectCustomAvatar', file);
      // Clear the input so picking the same file twice still fires a change.
      input.value = '';
    },
    submit() {
      this.$store.dispatch('onBoarding/submitProfilePhoto');
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>

<style scoped>
.auth-heading {
  font-family: 'Montserrat', Helvetica, Arial, sans-serif;
  font-size: clamp(1.4rem, 2.4vw, 1.75rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: #fff;
  margin: 0 0 0.35rem;
}
.auth-sub {
  color: var(--pro-text-muted);
  font-size: 0.9rem;
  margin: 0 0 0.9rem;
}
/* Six fixed columns: 17 avatars land in three tidy rows that fit the panel
   without scrolling, which is the whole point of the layout. */
.presets {
  list-style: none;
  margin: 0 0 0.75rem;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
}
.preset {
  width: 100%;
  aspect-ratio: 1;
  padding: 2px;
  border-radius: 50%;
  border: 2px solid transparent;
  background: rgba(255, 255, 255, 0.04);
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.preset:hover { background: rgba(255, 255, 255, 0.1); }
.preset--selected { border-color: var(--yellow, #fddf00); }
.preset img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: block;
}

/* The "Add photo" cell: same circle as an avatar, muted until a photo is in it. */
.preset--add {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.07);
  border: 1px dashed var(--pro-border-strong);
  text-align: center;
  overflow: hidden;
}
.preset--add:hover { background: rgba(255, 255, 255, 0.12); }
.preset--add.preset--selected { border-style: solid; }
.preset__add-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.preset__add-icon {
  font-size: 18px;
  color: var(--pro-text-muted);
}
.preset__add-label {
  color: var(--pro-text-muted);
  font-size: 0.55rem;
  font-weight: 700;
  line-height: 1.1;
  padding: 0 2px;
}
.preset--add:hover .preset__add-icon,
.preset--add:hover .preset__add-label { color: #fff; }
/* Visually hidden but still focusable, so the cell is reachable by keyboard. */
.preset__input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}
.preset--add:focus-within { border-color: var(--yellow, #fddf00); }
</style>
